// src/api/dialoguesApi.js
import axios from 'axios';
import { ERROR_CODES } from '../constants/errors'; // Import

const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// --- Google ---

// 2) GOOGLE CALLBACK (Handles OAuth callback and calls backend)
export const connectGoogle = async (token, params) => {
  const { code, state } = params;
  const url = `${baseUrl}/google/api/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return { status: true, message: response.data.message }; // Success
    } else {
      // Use centralized error codes
      return {
        status: false,
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
      };
    }
  } catch (error) {
    console.error('[connectGoogle] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

// 3) REFRESH GOOGLE TOKEN (Marks as invalid, re-auth needed)
export const refreshGoogleToken = async (token, params) => {
  const { accountId } = params;
  const url = `${baseUrl}/google/api/${accountId}/refresh`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return { newToken: response.data.newToken };
    } else {
      // Use centralized error codes
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[refreshGoogleToken] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

// 4) DISCONNECT GOOGLE
export const disconnectGoogle = async (token, params) => {
  const { accountId } = params;
  const url = `${baseUrl}/google/api/${accountId}/disconnect`;
  try {
    const response = await axios.delete(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return { status: true, message: response.data.message }; // Success
    } else {
      // Use centralized error codes
      return {
        status: false,
        error: response.data.error || `HTTP_ERROR_${response.data.status_code}`,
        message: response.data.message || 'An unexpected error occurred.',
      };
    }
  } catch (error) {
    console.error('[disconnectGoogle] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

// 5) GOOGLE STATUS
export const getGoogleStatus = async (token, params) => {
  const { accountId } = params;
  const url = `${baseUrl}/google/api/${accountId}/status`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return response.data;
    } else {
      // Use centralized error codes
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[getGoogleStatus] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

// 6) LIST GOOGLE ACCOUNTS
export const listGoogleAccounts = async (token) => {
  const url = `${baseUrl}/google/api/list`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return response.data;
    } else {
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[listGoogleAccounts] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

// MICROSOFT 365

export const connectMicrosoft365 = async (token, params) => {
  const { code, state } = params;
  const url = `${baseUrl}/microsoft/api/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return { status: true, message: response.data.message }; // Success
    } else {
      return {
        status: false,
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
      };
    }
  } catch (error) {
    console.error('[connectMicrosoft365] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

export const refreshMicrosoft365Token = async (token, params) => {
  const { accountId } = params;
  const url = `${baseUrl}/microsoft/api/${accountId}/refresh`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return {
        newToken: response.data.newToken,
        expiresIn: response.data.expiresIn,
      };
    } else {
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error(
      '[refreshMicrosoft365Token] Network or request error:',
      error,
    );
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

export const disconnectMicrosoft365 = async (token, params) => {
  const { accountId } = params;
  const url = `${baseUrl}/microsoft/api/${accountId}/disconnect`;
  try {
    const response = await axios.delete(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return { status: true, message: response.data.message }; // Success
    } else {
      return {
        status: false,
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
      };
    }
  } catch (error) {
    console.error('[disconnectMicrosoft365] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

export const getMicrosoft365Status = async (token, params) => {
  const { accountId } = params;
  const url = `${baseUrl}/microsoft/api/${accountId}/status`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return response.data;
    } else {
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[getMicrosoft365Status] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

export const listMicrosoft365Accounts = async (token) => {
  const url = `${baseUrl}/microsoft/api/list`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return response.data;
    } else {
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error(
      '[listMicrosoft365Accounts] Network or request error:',
      error,
    );
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

// --- YouTube ---

/**
 * 1) CONNECT YOUTUBE
 * Sends the authorization code and state received by the frontend to the backend.
 * The backend then exchanges it for tokens.
 * @param {string} token - The user's Firebase auth token.
 * @param {object} params - Contains { code, state }.
 * @returns {Promise<object>}
 */
export const connectYouTube = async (token, params) => {
  const { code, state } = params;
  const url = `${baseUrl}/youtube/api/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return { status: true, message: response.data.message }; // Success
    } else {
      return {
        status: false,
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
      };
    }
  } catch (error) {
    console.error('[connectYouTube] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

/**
 * 2) REFRESH YOUTUBE TOKEN
 * Triggers the backend to refresh an expired access token using the stored refresh token.
 * @param {string} token - The user's Firebase auth token.
 * @param {object} params - Contains { accountId } (the YouTube Channel ID).
 * @returns {Promise<object>}
 */
export const refreshYouTubeToken = async (token, params) => {
  const { accountId } = params;
  const url = `${baseUrl}/youtube/api/${accountId}/refresh`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return {
        newToken: response.data.newToken,
        expiresIn: response.data.expiresIn,
      };
    } else {
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[refreshYouTubeToken] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

/**
 * 3) DISCONNECT YOUTUBE
 * Tells the backend to delete the credentials for a specific YouTube channel.
 * @param {string} token - The user's Firebase auth token.
 * @param {object} params - Contains { accountId } (the YouTube Channel ID).
 * @returns {Promise<object>}
 */
export const disconnectYouTube = async (token, params) => {
  const { accountId } = params;
  const url = `${baseUrl}/youtube/api/${accountId}/disconnect`;
  try {
    const response = await axios.delete(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return { status: true, message: response.data.message }; // Success
    } else {
      return {
        status: false,
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
      };
    }
  } catch (error) {
    console.error('[disconnectYouTube] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

/**
 * 4) YOUTUBE STATUS
 * Fetches the connection status for a specific YouTube channel.
 * @param {string} token - The user's Firebase auth token.
 * @param {object} params - Contains { accountId } (the YouTube Channel ID).
 * @returns {Promise<object>}
 */
export const getYouTubeStatus = async (token, params) => {
  const { accountId } = params;
  const url = `${baseUrl}/youtube/api/${accountId}/status`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return response.data;
    } else {
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[getYouTubeStatus] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

/**
 * 5) LIST YOUTUBE ACCOUNTS
 * Fetches all connected YouTube accounts for the current user.
 * @param {string} token - The user's Firebase auth token.
 * @returns {Promise<object>}
 */
export const listYouTubeAccounts = async (token) => {
  const url = `${baseUrl}/youtube/api/list`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      // Expects a response like { youtubeAccounts: [...] }
      return response.data;
    } else {
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[listYouTubeAccounts] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

// --- Meta ---

// 1) CONNECT META (Initiates OAuth flow)
export const connectMeta = async (token, params) => {
  const { network, accountId } = params;
  const url = `${baseUrl}/meta/api/${network}/connect`;
  try {
    const response = await axios.post(
      url,
      { accountIdentifier: accountId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        validateStatus: (status) => status >= 200 && status < 500,
      },
    );

    if (response.status === 200) {
      return response.data;
    } else {
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[connectMeta] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

// 2) META CALLBACK
//   Handled directly in SocialMediaSettings.js (no API call needed here)

// 3) REFRESH META TOKEN (Marks as invalid, re-auth needed)
export const refreshMetaToken = async (token, params) => {
  const { network, accountId } = params;
  const url = `${baseUrl}/meta/api/${network}/${accountId}/refresh`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return response.data;
    } else {
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[refreshMetaToken] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

// 4) DISCONNECT META
export const disconnectMeta = async (token, params) => {
  const { network, accountId } = params;
  const url = `${baseUrl}/meta/api/${network}/${accountId}/disconnect`;
  try {
    const response = await axios.delete(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return response.data;
    } else {
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[disconnectMeta] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

// 5) META STATUS
export const getMetaStatus = async (token, params) => {
  const { network, accountId } = params;
  const url = `${baseUrl}/meta/api/${network}/${accountId}/status`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return response.data;
    } else {
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[getMetaStatus] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

// 6) LIST META ACCOUNTS
export const listMetaAccounts = async (token, params) => {
  const { network } = params;
  const url = `${baseUrl}/meta/api/${network}/list`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return response.data;
    } else {
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[listMetaAccounts] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

// --- Telegram ---

// 1) CONNECT TELEGRAM
export const connectTelegram = async (token, params) => {
  const url = `${baseUrl}/teleauth/api/connect`;
  try {
    const response = await axios.post(url, params, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return response.data;
    } else {
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[connectTelegram] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

// 3) TELEGRAM STATUS
export const getTelegramStatus = async (token, params) => {
  const { accountIdentifier } = params;
  const url = `${baseUrl}/teleauth/api/${accountIdentifier}/status`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return response.data;
    } else {
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[getTelegramStatus] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

// 4) LIST TELEGRAM ACCOUNTS
export const listTelegramAccounts = async (token) => {
  const url = `${baseUrl}/teleauth/api/list`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      console.log('[listTelegramAccounts]response.data = ', response.data);
      return response.data;
    } else {
      console.log(
        '[listTelegramAccounts]Error message = ',
        response.data.message,
      );
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[listTelegramAccounts] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

export async function initTelegramAuth(token, params) {
  const url = `${baseUrl}/teleauth/api/initAuth`;
  try {
    const body = {
      phoneNumber: params.phoneNumber,
    };

    const response = await axios.post(url, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      // response.data => e.g. { status: "CODE_REQUIRED", ephemeralSession: "..." }
      return {
        status: true,
        data: response.data, // pass along {status: "CODE_REQUIRED", ephemeralSession: "..."}
      };
    } else {
      // e.g. { error: "...", message: "..." }
      return {
        status: false,
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
      };
    }
  } catch (error) {
    console.error('[initTelegramAuth] Request error:', error);
    if (error.response) {
      return {
        status: false,
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        status: false,
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return {
        status: false,
        error: ERROR_CODES.REQUEST_ERROR,
        message: error.message,
      };
    }
  }
}

export async function completeTelegramAuth(token, params) {
  const url = `${baseUrl}/teleauth/api/completeAuth`;
  try {
    const body = {
      code: params.code || '',
    };

    const response = await axios.post(url, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      // Might be {message: "Telegram Authentication completed!"}
      // or {status: "PASSWORD_REQUIRED"}
      return {
        status: true,
        data: response.data, // pass along the actual object
      };
    } else {
      return {
        status: false,
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
      };
    }
  } catch (error) {
    console.error('[completeTelegramAuth] Request error:', error);
    if (error.response) {
      return {
        status: false,
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        status: false,
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return {
        status: false,
        error: ERROR_CODES.REQUEST_ERROR,
        message: error.message,
      };
    }
  }
}

export async function completeTelegram2FAAuth(token, params) {
  const url = `${baseUrl}/teleauth/api/complete2FAAuth`;
  try {
    const body = {
      password: params.password || '',
    };

    const response = await axios.post(url, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      // Might be {message: "Telegram Authentication completed!"}
      // or {status: "PASSWORD_REQUIRED"}
      return {
        status: true,
        data: response.data, // pass along the actual object
      };
    } else {
      return {
        status: false,
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
      };
    }
  } catch (error) {
    console.error('[completeTelegram2FAAuth] Request error:', error);
    if (error.response) {
      return {
        status: false,
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        status: false,
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return {
        status: false,
        error: ERROR_CODES.REQUEST_ERROR,
        message: error.message,
      };
    }
  }
}

export async function disconnectTelegram(token, params) {
  // params should contain something like { accountIdentifier: "the phone or user handle" }
  const url = `${baseUrl}/teleauth/api/disconnectTelegram`;
  try {
    // We create the request body
    const requestBody = {
      accountIdentifier: params.accountIdentifier || '',
    };

    const response = await axios.post(url, requestBody, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      // success
      return {
        status: true,
        message: response.data.message,
      };
    } else {
      // handle back-end error object
      return {
        status: false,
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
      };
    }
  } catch (error) {
    console.error('[disconnectTelegram] Request error:', error);
    if (error.response) {
      return {
        status: false,
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        status: false,
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return {
        status: false,
        error: ERROR_CODES.REQUEST_ERROR,
        message: error.message,
      };
    }
  }
}

export const fetchSocialCredentials = async (token) => {
  try {
    // Launch all list requests in parallel for maximum efficiency
    const [
      googleAccounts,
      microsoft365Accounts,
      youtubeAccounts,
      // facebookAccounts,
      // instagramAccounts,
      // whatsappAccounts,
      telegramAccounts,
    ] = await Promise.all([
      listGoogleAccounts(token),
      listMicrosoft365Accounts(token),
      listYouTubeAccounts(token), // <-- ADD THE CALL TO THE NEW YOUTUBE FUNCTION
      // listMetaAccounts(token, { network: 'facebook' }),
      // listMetaAccounts(token, { network: 'instagram' }),
      // listMetaAccounts(token, { network: 'whatsappCloud' }),
      listTelegramAccounts(token),
    ]);

    console.log('[fetchSocialCredentialsAPI] Google Accounts:', googleAccounts);
    console.log(
      '[fetchSocialCredentialsAPI] Microsoft 365 Accounts:',
      microsoft365Accounts,
    );
    console.log(
      '[fetchSocialCredentialsAPI] YouTube Accounts:',
      youtubeAccounts,
    ); // For debugging
    console.log(
      '[fetchSocialCredentialsAPI] Telegram Accounts:',
      telegramAccounts,
    );

    // Combine all accounts into a single array, mapping each to a standard format
    const allAccounts = [
      ...(googleAccounts?.googleAccounts || []).map((acc) => ({
        ...acc,
        socialNetwork: 'gmail',
      })),
      ...(microsoft365Accounts?.microsoftAccounts || []).map((acc) => ({
        // <-- ADDED MICROSOFT
        ...acc,
        socialNetwork: 'microsoft365',
      })),
      ...(youtubeAccounts?.youtubeAccounts || []).map((acc) => ({
        ...acc,
        socialNetwork: 'youtube',
      })), // <-- ADD THE YOUTUBE RESULTS
      // ...(facebookAccounts?.facebookAccounts || []).map(acc => ({ ...acc, socialNetwork: 'facebook' })),
      // ...(instagramAccounts?.instagramAccounts || []).map(acc => ({ ...acc, socialNetwork: 'instagram' })),
      // ...(whatsappAccounts?.whatsappCloudAccounts || []).map(acc => ({ ...acc, socialNetwork: 'whatsappCloud' })),
      ...(telegramAccounts?.telegramAccounts || []).map((acc) => ({
        ...acc,
        socialNetwork: 'telegram',
      })),
    ];

    return allAccounts;
  } catch (error) {
    // For consistency, return always an object in case of a catastrophic failure
    console.error('[fetchSocialCredentials] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

/* -------------------------------------------------------------------------- */
/*  1.  Per-platform AI summary generators                                               */
/* -------------------------------------------------------------------------- */

export const generateGmailSummary = async (token, params) => {
  const url = `${baseUrl}/gmail/api/summarizeGmailMessages`;
  try {
    const gmailParams = {
      influencerId: params.influencerId,
      userHandles: params.userHandles?.gmail ?? [],
      influencerHandles: params.influencerHandles?.gmail ?? [],
    };

    const response = await axios.post(url, gmailParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (s) => s >= 200 && s < 500,
    });

    if (response.status === 200) {
      return { status: 'success', data: response.data };
    } else if (response.status === 404) {
      return {
        status: 'empty',
        data: response.data || {},
        message: response.data?.message || 'No data available',
      };
    }
    return {
      status: 'error',
      error: response.data?.error || `HTTP_ERROR_${response.status}`,
      message: response.data?.message || 'An unexpected error occurred.',
    };
  } catch (error) {
    console.error('[generateGmailSummary] Network or request error:', error);
    if (error.response) {
      // Server responded with a status code outside the 2xx range
      return {
        status: 'error',
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data?.message || 'API request failed.',
      };
      // --- MODIFICATION START ---
    } else if (error.request) {
      // The request was made but no response was received (e.g., network error, timeout)
      return {
        status: 'error',
        error: ERROR_CODES.NETWORK_ERROR, // Use a consistent error code
        message:
          'Could not reach the server. Please check your internet connection.',
      };
      // --- MODIFICATION END ---
    }
    // Something happened in setting up the request that triggered an Error
    return {
      status: 'error',
      error: ERROR_CODES.REQUEST_ERROR,
      message: error.message,
    };
  }
};

// ------------------ Microsoft 365 --------------------------------------
export const generateMicrosoft365Summary = async (token, params) => {
  const url = `${baseUrl}/microsoft365/api/summarizeMicrosoftMessages`;
  try {
    const msParams = {
      influencerId: params.influencerId,
      userHandles: params.userHandles?.microsoft365 ?? [],
      influencerHandles: params.influencerHandles?.microsoft365 ?? [],
    };

    const response = await axios.post(url, msParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (s) => s >= 200 && s < 500,
    });

    if (response.status === 200) {
      return { status: 'success', data: response.data };
    } else if (response.status === 404) {
      return {
        status: 'empty',
        data: response.data || {},
        message: response.data?.message || 'No data available',
      };
    }
    return {
      status: 'error',
      error: response.data?.error || `HTTP_ERROR_${response.status}`,
      message: response.data?.message || 'An unexpected error occurred.',
    };
  } catch (error) {
    console.error(
      '[generateMicrosoft365Summary] Network or request error:',
      error,
    );
    if (error.response) {
      return {
        status: 'error',
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data?.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        status: 'error',
        error: ERROR_CODES.NETWORK_ERROR,
        message:
          'Could not reach the server. Please check your internet connection.',
      };
    }
    return {
      status: 'error',
      error: ERROR_CODES.REQUEST_ERROR,
      message: error.message,
    };
  }
};

// ---------- Facebook --------------------------------------------------------
export const generateFacebookSummary = async (token, params) => {
  const url = `${baseUrl}/meta/api/summarizeFacebookMessages`;
  try {
    const facebookParams = {
      influencerId: params.influencerId,
      userHandles: params.userHandles?.facebook ?? [],
      influencerHandles: params.influencerHandles?.facebook ?? [],
    };

    const response = await axios.post(url, facebookParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (s) => s >= 200 && s < 500,
    });

    if (response.status === 200) {
      return { status: 'success', data: response.data };
    } else if (response.status === 404) {
      return {
        status: 'empty',
        data: response.data || {},
        message: response.data?.message || 'No data available',
      };
    }
    return {
      status: 'error',
      error: response.data?.error || `HTTP_ERROR_${response.status}`,
      message: response.data?.message || 'An unexpected error occurred.',
    };
  } catch (error) {
    console.error('[generateFacebookSummary] Network or request error:', error);
    if (error.response) {
      return {
        status: 'error',
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data?.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        status: 'error',
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    }
    return {
      status: 'error',
      error: ERROR_CODES.REQUEST_ERROR,
      message: error.message,
    };
  }
};

// ---------- Instagram -------------------------------------------------------
export const generateInstagramSummary = async (token, params) => {
  const url = `${baseUrl}/meta/api/summarizeInstagramMessages`;
  try {
    const instagramParams = {
      influencerId: params.influencerId,
      userHandles: params.userHandles?.instagram ?? [],
      influencerHandles: params.influencerHandles?.instagram ?? [],
    };

    const response = await axios.post(url, instagramParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (s) => s >= 200 && s < 500,
    });

    if (response.status === 200) {
      return { status: 'success', data: response.data };
    } else if (response.status === 404) {
      return {
        status: 'empty',
        data: response.data || {},
        message: response.data?.message || 'No data available',
      };
    }
    return {
      status: 'error',
      error: response.data?.error || `HTTP_ERROR_${response.status}`,
      message: response.data?.message || 'An unexpected error occurred.',
    };
  } catch (error) {
    console.error(
      '[generateInstagramSummary] Network or request error:',
      error,
    );
    if (error.response) {
      return {
        status: 'error',
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data?.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        status: 'error',
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    }
    return {
      status: 'error',
      error: ERROR_CODES.REQUEST_ERROR,
      message: error.message,
    };
  }
};

// ---------- WhatsApp --------------------------------------------------------
export async function generateWhatsappSummary(token, params) {
  const url = `${baseUrl}/whatsapp/api/summarizeWhatsappMessages`;
  try {
    const whatsappParams = {
      influencerId: params.influencerId,
      userHandles: params.userHandles?.whatsapp ?? [],
      influencerHandles: params.influencerHandles?.whatsapp ?? [],
    };

    const response = await axios.post(url, whatsappParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (s) => s >= 200 && s < 500,
    });

    if (response.status === 200) {
      return { status: 'success', data: response.data };
    } else if (response.status === 404) {
      return {
        status: 'empty',
        data: response.data || {},
        message: response.data?.message || 'No data available',
      };
    }
    return {
      status: 'error',
      error: response.data?.error || `HTTP_ERROR_${response.status}`,
      message: response.data?.message || 'An unexpected error occurred.',
    };
  } catch (error) {
    console.error('[generateWhatsappSummary] Network or request error:', error);
    if (error.response) {
      return {
        status: 'error',
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data?.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        status: 'error',
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    }
    return {
      status: 'error',
      error: ERROR_CODES.REQUEST_ERROR,
      message: error.message,
    };
  }
}

// ---------- Telegram --------------------------------------------------------
export async function generateTelegramSummary(token, params) {
  const url = `${baseUrl}/telegram/api/summarizeTelegramMessages`;
  try {
    const tgParams = {
      influencerId: params.influencerId,
      userHandles: params.userHandles?.telegram ?? [],
      influencerHandles: params.influencerHandles?.telegram ?? [],
    };

    const response = await axios.post(url, tgParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (s) => s >= 200 && s < 500,
    });

    if (response.status === 200) {
      return { status: 'success', data: response.data };
    } else if (response.status === 404) {
      return {
        status: 'empty',
        data: response.data || {},
        message: response.data?.message || 'No data available',
      };
    }
    return {
      status: 'error',
      error: response.data?.error || `HTTP_ERROR_${response.status}`,
      message: response.data?.message || 'An unexpected error occurred.',
    };
  } catch (error) {
    console.error('[generateTelegramSummary] Network or request error:', error);
    if (error.response) {
      return {
        status: 'error',
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data?.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        status: 'error',
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    }
    return {
      status: 'error',
      error: ERROR_CODES.REQUEST_ERROR,
      message: error.message,
    };
  }
}

/* -------------------------------------------------------------------------- */
/*  2.  Orchestrator                                                          */
/* -------------------------------------------------------------------------- */

export const generateMessagesSummary = async (token, params) => {
  try {
    // 2-a. Map platforms to their generators
    const platformMap = {
      gmail: generateGmailSummary,
      microsoft365: generateMicrosoft365Summary,
      facebook: generateFacebookSummary,
      instagram: generateInstagramSummary,
      whatsapp: generateWhatsappSummary,
      telegram: generateTelegramSummary,
    };

    // 2-b. Keep only platforms with at least one handle
    const activePlatforms = Object.keys(platformMap).filter((platform) => {
      const hasUser =
        Array.isArray(params.userHandles?.[platform]) &&
        params.userHandles[platform].length > 0;
      const hasInfluencer =
        Array.isArray(params.influencerHandles?.[platform]) &&
        params.influencerHandles[platform].length > 0;
      return hasUser || hasInfluencer;
    });

    if (activePlatforms.length === 0) {
      return {
        error: ERROR_CODES.FAILED_TO_GENERATE_SUMMARY,
        message: 'No platform handles provided.',
      };
    }

    // 2-c. Launch requests only for active platforms
    const apiCalls = (activePlatforms || []).map((p) =>
      platformMap[p](token, params),
    );
    const results = await Promise.all(apiCalls); // promises never reject

    // 4. Tag each result with its platform
    const taggedResults = results.map((res, idx) => ({
      platform: activePlatforms[idx] ?? '',
      result: res,
    }));

    /* ------------ Categorise outcomes ---------------- */
    const successfulResponses = taggedResults.filter(
      (t) => t.result.status === 'success',
    );
    const numSuccessful = successfulResponses.length;

    const errors = taggedResults
      .filter((t) => t.result.status === 'error')
      .map((t) => ({
        code: t.result.error ?? '',
        message: t.result.message ?? '',
      }));

    const emptyMessages = taggedResults
      .filter((t) => t.result.status === 'empty')
      .map((t) => ({
        platform: t.platform ?? '',
        message: t.result.message ?? '',
      }));

    /* ------------ Decide output shape ---------------- */
    if (numSuccessful === 0) {
      return {
        error: ERROR_CODES.FAILED_TO_GENERATE_SUMMARY,
        message: 'No summaries could be generated.',
        errors,
        emptyMessages,
      };
    }

    if (numSuccessful === 1) {
      return {
        summary: successfulResponses[0].result.data,
        emptyMessages,
      };
    }
    //    if (numSuccessful === 1) {
    //      return {
    //        ...successfulResponses[0].result,
    //        emptyMessages,
    //      };
    //    }

    const combinedResult = await combineSummaries(
      token,
      params,
      successfulResponses.map((t) => t.result.data ?? ''),
    );

    return {
      ...combinedResult,
      errors,
      emptyMessages,
    };
  } catch (error) {
    return {
      error: ERROR_CODES.INTERNAL_SERVER_ERROR,
      message: 'An unexpected error occurred.',
    };
  }
};

// --- Backend API Call to Combine Summaries ---

async function combineSummaries(token, params, detailsToCombine) {
  const url = `${baseUrl}/messages/api/combineSummaries`; // NEW ENDPOINT
  try {
    const payload = {
      influencerId: params.influencerId,
      details: detailsToCombine,
    };
    const response = await axios.post(url, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return response.data; // Return the *entire* object
    } else {
      // Consistent error object
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[combineSummaries] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
}

export const fetchMessagesSummary = async (token, influencerId) => {
  const url = `${
    process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'
  }/messages/api/fetchSummary/${influencerId}`;

  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return response.data;
    } else {
      // Consistent error object
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || 'An unexpected error occurred.',
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[fetchMessagesSummary] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};
